// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useState } from 'react';
import {
  closestCenter, CollisionDetection, DragEndEvent, DragStartEvent, rectIntersection,
} from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { moveProjectToFolderThunk, reorderFoldersThunk } from 'Features/folderList/folderListThunk';
import moveFolder from 'Features/folderList/folderOrder';
import { SetFoldersAction } from 'Features/folderList/folderListActions';
import { findFolderById, selectFolderList } from 'Features/folderList/folderListSelector';
import { AssignProjectToFolderAction } from './projectListActions';
import { useDashboardDragSensors } from './dragSensors';

// Folder drags snap to the nearest slot so the preview gap opens as the card approaches it; a
// project has to be over a folder before it is filed into it.
export const dashboardCollisionDetection: CollisionDetection = (args) => (
  args.active.data.current?.type === 'folder' ? closestCenter(args) : rectIntersection(args)
);

// Once a project has been filed into a folder - by a drop or the Move To dialog - the cached set
// takes the new membership, so the card's badge and the folder filter follow at once.
export const useAssignProjectToFolder = () => {
  const dispatch = useAppDispatch();
  return async (project?: IProject, folderId?: string) => {
    if (!project?._id) return;
    await dispatch(AssignProjectToFolderAction({ projectId: project._id, folderId }));
  };
};

// The drags the dashboard's grid and the map rail share (AUT-5209, AUT-6509): a folder dropped on
// another folder reorders the folders (shown at once, then saved for the organization), a project
// dropped on a folder is filed into it.
export const useDashboardDrag = () => {
  const dispatch = useAppDispatch();
  const folderList = useAppSelector(selectFolderList);
  const assignProjectToFolder = useAssignProjectToFolder();
  const sensors = useDashboardDragSensors();
  // The folder being dragged, drawn by the drag overlay while its in-flow card is the placeholder
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const activeFolder = findFolderById(folderList, activeFolderId ?? undefined);

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveFolderId(active.data.current?.type === 'folder' ? String(active.id) : null);
  };

  const onDragCancel = () => setActiveFolderId(null);

  const reorderFolders = async (activeId: string, overId: string) => {
    const moved = moveFolder(folderList, activeId, overId);
    if (moved === folderList) return;
    dispatch(SetFoldersAction(moved));
    await dispatch(reorderFoldersThunk(moved.map((folder: IFolder) => folder._id)));
  };

  const fileProjectIntoFolder = async (projectId: string, folderId: string) => {
    try {
      const response = await dispatch(moveProjectToFolderThunk(projectId, folderId));
      if (response.success) await assignProjectToFolder(response.data as IProject, folderId);
    } catch (error) {
      // Nothing moved - the thunk throws before any local change - but the drop looks as if it
      // did nothing, so the failure is at least on record.
      console.error('Moving the project to the folder failed', error);
    }
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveFolderId(null);
    if (!active.id || !over?.id) return;
    if (active.data.current?.type === 'folder') {
      await reorderFolders(String(active.id), String(over.id));
      return;
    }
    await fileProjectIntoFolder(String(active.id), String(over.id));
  };

  return { sensors, activeFolder, onDragStart, onDragEnd, onDragCancel };
};

export default useDashboardDrag;
